import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Iter 31.254 — Mureka library catalogue admin tile.
 * Surfaces the current row count + sample first row, plus a re-import
 * button that triggers the TSV → Mongo upsert. Idempotent — safe to
 * click multiple times.
 */
export default function MurekaLibraryTile() {
    const [count, setCount] = useState(null);
    const [sample, setSample] = useState(null);
    const [running, setRunning] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [error, setError] = useState(null);

    const refresh = async () => {
        try {
            const { data } = await api.get("/admin/mureka-library/count");
            setCount(data.count);
            setSample(data.first_row_sample);
            setError(null);
        } catch (e) {
            setError(String(e?.response?.data?.detail || e.message || e));
        }
    };

    useEffect(() => {
        let live = true;
        (async () => {
            try {
                const { data } = await api.get("/admin/mureka-library/count");
                if (!live) return;
                setCount(data.count);
                setSample(data.first_row_sample);
            } catch (e) {
                if (!live) return;
                setError(String(e?.response?.data?.detail || e.message || e));
            }
        })();
        return () => { live = false; };
    }, []);

    const runImport = async () => {
        if (running) return;
        setRunning(true);
        setError(null);
        try {
            const { data } = await api.post("/admin/mureka-library/import");
            setLastResult(data);
            await refresh();
        } catch (e) {
            setError(String(e?.response?.data?.detail || e.message || e));
        } finally {
            setRunning(false);
        }
    };

    return (
        <div
            className="paper-card border border-ink/30 p-4 mb-4"
            data-testid="admin-mureka-library-tile"
        >
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <div>
                    <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                        Mureka Library · Catalogue
                    </h3>
                    <p className="font-body text-xs text-ink-soft mt-1 max-w-2xl">
                        Iter 31.254 — share-link catalogue stored in
                        <code className="px-1">db.mureka_library</code>. Idempotent
                        upsert by mureka_song_id. Separate from canon song
                        commissions in the life-entries ledger.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={runImport}
                    disabled={running}
                    data-testid="mureka-library-reimport-btn"
                    className="font-body text-xs uppercase tracking-[0.18em] px-3 py-1 border border-ink/40 hover:bg-ink/5"
                >
                    {running ? "Importing…" : "Re-import TSV"}
                </button>
            </div>

            <div
                className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 font-body text-xs text-ink"
                data-testid="mureka-library-stats"
            >
                <div>
                    <div className="text-ink-soft uppercase tracking-[0.16em] text-[10px]">
                        Songs stored
                    </div>
                    <div className="text-2xl tabular-nums">
                        {count === null ? "…" : count}
                    </div>
                </div>
                {sample && (
                    <div className="sm:col-span-2">
                        <div className="text-ink-soft uppercase tracking-[0.16em] text-[10px]">
                            Row 1 sample
                        </div>
                        <div className="text-ink truncate">
                            #{sample.row_number} · {sample.title}
                        </div>
                        {sample.mureka_url && (
                            <a
                                href={sample.mureka_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[10px] text-ink-soft underline truncate block"
                            >
                                {sample.mureka_url.slice(0, 70)}…
                            </a>
                        )}
                    </div>
                )}
            </div>

            {lastResult && (
                <div
                    className="mt-3 font-body text-[10px] text-ink-soft"
                    data-testid="mureka-library-last-result"
                >
                    Last import · scanned={lastResult.scanned} ·
                    inserted={lastResult.inserted} ·
                    updated={lastResult.updated} ·
                    skipped={lastResult.skipped}
                </div>
            )}
            {error && (
                <div
                    className="mt-2 font-body text-xs text-stamp-red"
                    data-testid="mureka-library-error"
                >
                    {error}
                </div>
            )}
        </div>
    );
}
